import { api, ApiError, tokenStore, tryRefresh } from '../../shared/api/client';
import { isAdminSession, type Session } from '../../shared/session';

/**
 * Отказ входа, который повтор не лечит: аккаунт есть, но роли `admin` у него нет.
 * Токены при этом стираются — сессия человека без права входа не должна пережить экран.
 */
export class NotAnAdministrator extends Error {
  constructor() {
    super('У этого аккаунта нет роли администратора.');
    this.name = 'NotAnAdministrator';
  }
}

type TokenPair = { accessToken: string; refreshToken: string };
type LoginBody = { tokens: TokenPair; session: Session };

/**
 * Вход по email и паролю (`POST /auth/login`, CAP-1). Роли приходят этим же ответом —
 * отдельного `GET /me` не нужно. Аккаунт без `admin` получает понятный отказ.
 */
export async function loginWithPassword(email: string, password: string): Promise<Session> {
  const body = await api.post('/auth/login', { body: { email, password } });
  const login = readLogin(body);
  if (!login) {
    throw new Error('Ответ входа не по контракту: пары токенов в нём нет.');
  }
  return admitOnlyAdministrators(login);
}

/**
 * Восстановление после перезагрузки: refresh из хранилища вкладки обменивается на новую
 * пару (AD-A3). Ответ обмена несёт `user` с ролями, как и вход. `null` — восстанавливать
 * нечего или нечем: гейт показывает вход.
 */
export async function restoreSession(): Promise<Session | null> {
  if (!tokenStore.refresh) {
    return null;
  }
  // Обмен через клиент, а не через `api.post('/auth/refresh')`: у `/auth/*` нет повтора после
  // 401, и именно `tryRefresh` держит правило «один обмен в полёте».
  const refreshed = await tryRefresh();
  if (!refreshed) {
    return null;
  }
  // Ролей у обмена в `tryRefresh` нет — он хранит только пару. Их даёт `GET /me`.
  const me = await api.get('/me');
  return admitOnlyAdministrators({
    tokens: { accessToken: tokenStore.access ?? '', refreshToken: tokenStore.refresh ?? '' },
    session: { id: me.id, roles: me.roles },
  });
}

/** Выход (CAP-8): refresh-сессия отзывается на сервере, хранилище стирается всегда. */
export async function logout(): Promise<void> {
  const refresh = tokenStore.refresh;
  try {
    if (refresh) {
      await api.post('/auth/logout', { body: { refreshToken: refresh } });
    }
  } catch (error) {
    // Уже отозванный или истёкший refresh — `401`: сессии на сервере и так нет. Любой другой
    // отказ тоже не повод оставить токены в браузере: локальная сессия завершается.
    if (!(error instanceof ApiError)) {
      throw error;
    }
  } finally {
    tokenStore.clear();
  }
}

function admitOnlyAdministrators(login: LoginBody): Session {
  if (!isAdminSession(login.session)) {
    tokenStore.clear();
    throw new NotAnAdministrator();
  }
  tokenStore.set(login.tokens);
  return login.session;
}

function readLogin(body: unknown): LoginBody | null {
  const fields = asRecord(body);
  const user = asRecord(fields?.user);
  const roles = user?.roles;
  if (
    !isToken(fields?.accessToken) ||
    !isToken(fields.refreshToken) ||
    typeof user?.id !== 'string' ||
    !Array.isArray(roles) ||
    !roles.every((role) => typeof role === 'string')
  ) {
    return null;
  }
  return {
    tokens: { accessToken: fields.accessToken, refreshToken: fields.refreshToken },
    session: { id: user.id, roles: roles as string[] },
  };
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function isToken(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}
